import { Injectable, Injector } from "@angular/core";
import { ID } from "@common/interfaces/id";

import { IIssueContext, InterfaceDataContext, InterfaceModeContext } from "@common/utils/workflowDog";
import { IssueQuery } from "./issue/issue.query";

import { IBaseInterfaceData } from "@common/interfaces/base";
import { IIssue } from "@common/interfaces/issue";
import { IIssueTypeInterface, InterfaceNameValue } from "@common/interfaces/issueTypeInterface";
import { combineLatest, Observable, of } from "rxjs";
import { debounceTime, distinctUntilChanged, filter, map, switchMap } from "rxjs/operators";
import { InterfaceDataResolver } from './interfaceDataResolver.service';
import { IssueTypeInterfaceQuery } from "./issueTypeInterface/issueTypeInterface.query";
import { MetaInterfaceServiceProvider } from "./metaInterfaces/metaInterfaces.provider.service";
import { AbstractInterfaceDataQuery } from "./interfaceDataModel";
import { EntityStore, EntityState } from "@datorama/akita";
import { CrudService } from "./crudService";
import { IUser } from "@common/interfaces/user";
import { UserQuery } from "./user/user.query";
import { UserApiService } from "./user/userApi.service";

// Warn una volta sola per interfaccia: lo store meta mancante viene toccato da molti consumer
// (context, form, automatismi) e il log a raffica coprirebbe il resto della console.
const warnedUnavailableInterfaces = new Set<string>();
function warnUnavailableInterfaceOnce(interfaceName: string, reason: string): void {
  if (warnedUnavailableInterfaces.has(interfaceName)) {
    return;
  }
  warnedUnavailableInterfaces.add(interfaceName);
  console.warn(`[ISSUE CONTEXT] ${reason} per ${interfaceName}: interfaccia trattata come assente`);
}

@Injectable({
  providedIn: 'root'
})
export class IssueContextQuery {
  //TODO piccola zozzeria che andrebbe sistemata - anche se non sarà semplicissimo
  private _interfaceProvider: MetaInterfaceServiceProvider;

  constructor(private issueQuery: IssueQuery,
    private injector: Injector,
    private issueTypeInterfaceQuery: IssueTypeInterfaceQuery,
    private userQuery: UserQuery,
    private userApiService: UserApiService,
    private interfaceData: InterfaceDataResolver,
  ) {}

  private get interfaceProvider(): MetaInterfaceServiceProvider {
    // Lazy per rompere il ciclo di DI: risolto alla prima lettura invece che su queueMicrotask,
    // cosi' nessuna chiamata sincrona precoce puo' vederlo undefined.
    return this._interfaceProvider ??= this.injector.get(MetaInterfaceServiceProvider);
  }

  /**
   * Per le interfacce meta puo' tornare undefined (store non registrato, vedi
   * reconcileMetaInterfaceStores in app.initializer.ts): il chiamante deve degradare a "nessun dato".
   */
  private getQuery(interfaceName: InterfaceNameValue | string & {}): AbstractInterfaceDataQuery<IBaseInterfaceData> | undefined {
    if (!interfaceName.startsWith('_meta_')) {
      if (!this.interfaceData.isRegistered(interfaceName)) {
        warnUnavailableInterfaceOnce(interfaceName, 'query frontend non registrata');
        return undefined;
      }
      return this.interfaceData.getQuery(interfaceName);
    }
    const query = this.interfaceProvider.getQuery(interfaceName);
    if (!query) {
      warnUnavailableInterfaceOnce(interfaceName, 'store meta non registrato');
    }
    return query;
  }

  // storeNames$ riemette anche quando loadStores non aggiunge nulla: si filtra per contenuto.
  private registeredMetaStoreNames$(): Observable<string[]> {
    return this.interfaceProvider.storeNames$.pipe(
      distinctUntilChanged((prev, curr) => prev.length === curr.length && prev.every((name, i) => name === curr[i]))
    );
  }

  private selectUser$(userId: ID): Observable<IUser | undefined> {
    return userId ? this.userQuery.selectEntity(userId) : of(undefined);
  }

  private getService(interfaceName: InterfaceNameValue | string & {}): CrudService<IBaseInterfaceData, EntityStore<EntityState<IBaseInterfaceData, string>, IBaseInterfaceData, string>, AbstractInterfaceDataQuery<IBaseInterfaceData>> {
    return interfaceName.startsWith('_meta_') ? this.interfaceProvider.getService(interfaceName) : this.interfaceData.getService(interfaceName);
  }

  private getInterfaceModes(typeInterfaces: IIssueTypeInterface[]): InterfaceModeContext {
    return typeInterfaces.reduce((modes, typeInterface) => {
      modes[typeInterface.interfaceName] ??= {};
      modes[typeInterface.interfaceName][typeInterface.name] = typeInterface.implementationMode;
      return modes;
    }, {} as InterfaceModeContext);
  }

  getIssueContext(issueId: ID): IIssueContext {
    const issue = this.issueQuery.getEntity(issueId);
    let reporter: IUser;
    let assignee: IUser;
    if (issue.assigneeId) {
      assignee = this.userQuery.getEntity(issue.assigneeId);
    }
    if (issue.reporterId) {
      reporter = this.userQuery.getEntity(issue.reporterId);
    }
    const interfaceData: Partial<InterfaceDataContext> = {};
    //const interfaces = this.interfaceData.interfaceNames();
    const typeInterfaces = this.issueTypeInterfaceQuery.getByIssueTypeId(issue.typeId);
    for (const int of typeInterfaces) {
      const query = this.getQuery(int.interfaceName);
      // [] e non chiave assente: workflowDog (validateCreationRules) itera le chiavi senza guardia
      interfaceData[int.interfaceName] = query ? query.getByIssueId(issueId) : [];
    }

    return {
      issue,
      reporter,
      assignee,
      interfaceData: interfaceData as InterfaceDataContext,
      interfaceModes: this.getInterfaceModes(typeInterfaces),
    }
  }

  getIssueContextByTaskType(issueId: ID, typeId: ID): IIssueContext {
    const issue = this.issueQuery.getEntity(issueId);
    let reporter: IUser;
    let assignee: IUser;
    if (issue.assigneeId) {
      assignee = this.userQuery.getEntity(issue.assigneeId);
    }
    if (issue.reporterId) {
      reporter = this.userQuery.getEntity(issue.reporterId);
    }
    const interfaceData: Partial<InterfaceDataContext> = {};
    const issueTypeInterfaces = this.issueTypeInterfaceQuery.getByIssueTypeId(typeId)
    const interfaces = issueTypeInterfaces.map(typeInterfaces => typeInterfaces.interfaceName);
    for (const interfaceName of interfaces) {
      const query = this.getQuery(interfaceName);
      interfaceData[interfaceName] = query ? query.getByIssueId(issueId) : [];
    }

    return {
      issue,
      reporter,
      assignee,
      interfaceData: interfaceData as InterfaceDataContext,
      interfaceModes: this.getInterfaceModes(issueTypeInterfaces),
    }
  }

  selectIssueContextByTaskType$(issueId: ID, typeId: ID): Observable<IIssueContext> {
    return this.issueQuery.selectEntity(issueId).pipe(
      switchMap(
        issue => combineLatest([
          this.selectUser$(issue.assigneeId),
          this.selectUser$(issue.reporterId),
          combineLatest([
            this.issueTypeInterfaceQuery.selectByIssueTypeId$(typeId),
            // Se lo store meta viene registrato in ritardo (riconciliazione all'avvio, sync in
            // corso) il context si ricalcola invece di restare per sempre senza quell'interfaccia.
            this.registeredMetaStoreNames$(),
          ]).pipe(
            switchMap(([typeInterfaces]) => {
              if (!typeInterfaces.length) {
                return of([]);
              }
              return combineLatest(
                typeInterfaces.map(typeInterface => {
                  const query = this.getQuery(typeInterface.interfaceName);
                  if (!query) {
                    return of({ typeInterface, data: [] as IBaseInterfaceData[] });
                  }
                  return query.selectByIssueId$(issueId).pipe(
                    map(data => ({ typeInterface, data }))
                  );
                })
              )
            }),
          ),
        ]).pipe(
          map(([assignee, reporter, [...interfacesData]]) => {
            const interfaceData: Partial<InterfaceDataContext> = {};
            for (const { data, typeInterface } of interfacesData) {
              interfaceData[typeInterface.interfaceName] = data;
            }
            return {
              issue,
              reporter,
              assignee,
              interfaceData: interfaceData as InterfaceDataContext,
              interfaceModes: this.getInterfaceModes(interfacesData.map(({ typeInterface }) => typeInterface)),
            }
          })
        )
      )
    )
  }

  cleanIssueContext(issueId: ID,): void {
    for (const service of this.interfaceData.getAllServices()) {
      service.localRemoveByIssue(issueId);
    }
  }

  selectIssueContext$(issueId: ID): Observable<IIssueContext> {
    return this.issueQuery.selectEntity(issueId).pipe(
      filter(issue => !!issue),
      // canChangeState usa solo questi campi della issue: evitiamo di ricalcolare
      // tutto il context quando cambiano campi non rilevanti (titolo, priorità, ...)
      distinctUntilChanged((prev, curr) =>
        prev.suspension === curr.suspension &&
        prev.typeId === curr.typeId &&
        prev.stateId === curr.stateId &&
        prev.reporterId === curr.reporterId &&
        prev.assigneeId === curr.assigneeId &&
        prev.createdBy === curr.createdBy
      ),
      switchMap(
        issue => combineLatest([
          this.selectUser$(issue.assigneeId),
          this.selectUser$(issue.reporterId),
          combineLatest([
            this.issueTypeInterfaceQuery.selectByIssueTypeId$(issue.typeId),
            this.registeredMetaStoreNames$(),
          ]).pipe(
            switchMap(([typeInterfaces]) => {
              if (!typeInterfaces.length) {
                return of([]);
              }
              return combineLatest(
                typeInterfaces.map(typeInterface => {
                  const query = this.getQuery(typeInterface.interfaceName);
                  if (!query) {
                    return of({ typeInterface, interfaceData: [] as IBaseInterfaceData[] });
                  }
                  return query.selectByIssueId$(issue.id).pipe(
                    map(interfaceData => ({ typeInterface, interfaceData }))
                  );
                })
              );
            }),
            // le interfacce possono aggiornarsi a raffica (first_sync / socket):
            // raggruppiamo i cambiamenti ravvicinati per evitare ricalcoli troppo frequenti
            debounceTime(150),
          ),
        ]).pipe(
          map(([assignee, reporter, interfaceData]) => ({
            issue,
            reporter,
            assignee,
            interfaceData: interfaceData.reduce((acc: InterfaceDataContext, curr) => {
              acc[curr.typeInterface.interfaceName] = curr.interfaceData;
              return acc;
            }, {} as InterfaceDataContext),
            interfaceModes: this.getInterfaceModes(interfaceData.map(({ typeInterface }) => typeInterface)),
          }))
        )
      )
    )
  }
}


/**
   * DO NOT USE OUTSIDE THIS FILE
   * @param issue
   * @returns
   */
function isIssue(issue: any): issue is IIssue {
  return (issue.id && issue.stateId);
}
